import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import {
  listSubHeadConfigs,
  createSubHeadConfig,
  deleteSubHeadConfig,
  listAmountConfigs,
  saveAmountConfig,
  deleteAmountConfig,
  listTimeConfigs,
  saveTimeConfig,
  deleteTimeConfig,
} from "../../api/feeApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const feeHeadApi = createLookupApi("fee-heads");
const feeSubHeadApi = createLookupApi("fee-subheads");
const classApi = createLookupApi("classes");
const groupApi = createLookupApi("groups");
const categoryApi = createLookupApi("student-categories");

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Fee Configuration — folds digicampusbd's Fee Sub Name Setup, Fee Amount
 * Setup (+ its "multiple" bulk variant — confirmed dead code in the old app
 * itself: `MultipleFeeAmountConfigurationRequest` is declared but never used
 * by any real controller/service method, and its own template is even named
 * `multiple-fee-amount-configurationxx.html`, an "xx" suffix consistent with
 * every other confirmed-abandoned old-app template in this migration) and
 * Fee Time Setup pages into one, tab-switched (see FeeConfigService on the
 * backend for the same grouping).
 *
 * The Fee Amount Setup tab has no Academic Year filter — it previously had
 * one, which was a real, confirmed deviation: the real old app's own
 * `fee-amount-configuration` screen (its template's own ajax payload, and
 * `FeeAmountConfigurationService.saveFeeAmountConfiguration`) never reads or
 * writes an academic year at all, and this migration's own actual fee
 * calculation (`FeeInvoiceServiceImpl`) already correctly queries this same
 * table without a year filter too — only this admin screen had drifted from
 * both. Removed to match; see `FeeAmountConfigurationRepository` javadoc for
 * the full evidence trail.
 */
export default function FeeConfigPage() {
  const [tab, setTab] = useState("subhead");
  const [feeHeads, setFeeHeads] = useState([]);
  const [feeSubHeads, setFeeSubHeads] = useState([]);
  const [classes, setClasses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    Promise.all([feeHeadApi.list(), feeSubHeadApi.list(), classApi.list(), groupApi.list(), categoryApi.list()]).then(
      ([heads, subHeads, cls, grp, cat]) => {
        setFeeHeads(heads);
        setFeeSubHeads(subHeads);
        setClasses(cls);
        setGroups(grp);
        setCategories(cat);
      }
    );
  }, []);

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Fee Configuration</h1>
      </div>
      <Tabs
        tabs={[
          { key: "subhead", label: "Fee Sub-Head Setup" },
          { key: "amount", label: "Fee Amount Setup" },
          { key: "time", label: "Fee Time Setup" },
        ]}
        active={tab}
        onChange={setTab}
      />
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {tab === "subhead" && (
        <SubHeadTab feeHeads={feeHeads} feeSubHeads={feeSubHeads} setError={setError} setSuccess={setSuccess} />
      )}
      {tab === "amount" && (
        <AmountTab
          feeHeads={feeHeads}
          feeSubHeads={feeSubHeads}
          classes={classes}
          groups={groups}
          categories={categories}
          setError={setError}
          setSuccess={setSuccess}
        />
      )}
      {tab === "time" && (
        <TimeTab feeHeads={feeHeads} feeSubHeads={feeSubHeads} setError={setError} setSuccess={setSuccess} />
      )}
    </MainLayout>
  );
}

function SubHeadTab({ feeHeads, feeSubHeads, setError, setSuccess }) {
  const [feeHeadId, setFeeHeadId] = useState("");
  const [configs, setConfigs] = useState([]);
  const [selectedSubHeadId, setSelectedSubHeadId] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (feeHeads.length > 0 && !feeHeadId) setFeeHeadId(String(feeHeads[0].id));
  }, [feeHeads, feeHeadId]);

  const load = () => {
    if (!feeHeadId) return;
    setLoading(true);
    listSubHeadConfigs(feeHeadId)
      .then(setConfigs)
      .finally(() => setLoading(false));
  };
  useEffect(load, [feeHeadId]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await createSubHeadConfig({ feeHeadId: Number(feeHeadId), feeSubHeadId: Number(selectedSubHeadId), recurringVariableFees: recurring });
      setSuccess("Fee subhead configured.");
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save.");
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove "${row.feeSubHeadName}" from "${row.feeHeadName}"?`)) return;
    try {
      await deleteSubHeadConfig(row.configId);
      setSuccess("Removed.");
      setConfigs((prev) => prev.filter((c) => c.configId !== row.configId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  return (
    <>
      <div className="attendance-filters">
        <label>
          Fee Head
          <select value={feeHeadId} onChange={(e) => setFeeHeadId(e.target.value)}>
            {feeHeads.map((h) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <form onSubmit={handleAdd} className="user-form" style={{ marginBottom: 24 }}>
        <label>Fee Sub-Head</label>
        <select value={selectedSubHeadId} onChange={(e) => setSelectedSubHeadId(e.target.value)} required>
          <option value="" disabled>
            Select a subhead
          </option>
          {feeSubHeads.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <label className="checkbox-label">
          <input type="checkbox" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} />
          Recurring / variable fee (monthly, e.g. late fee)
        </label>
        <div className="form-actions">
          <button type="submit" className="btn-primary">
            Add
          </button>
        </div>
      </form>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[
            { key: "feeSubHeadName", label: "Sub-Head" },
            { key: "recurringVariableFees", label: "Recurring", render: (r) => (r.recurringVariableFees ? "Yes" : "No") },
          ]}
          rows={configs}
          actions={(row) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
              Remove
            </button>
          )}
        />
      )}
    </>
  );
}

function AmountTab({ feeHeads, feeSubHeads, classes, groups, categories, setError, setSuccess }) {
  const [classId, setClassId] = useState("");
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ groupId: "", categoryId: "", feeHeadId: "", feeSubHeadId: "", feeAmount: "", fineAmount: "" });

  useEffect(() => {
    if (classes.length > 0 && !classId) setClassId(String(classes[0].id));
  }, [classes, classId]);

  const load = () => {
    if (!classId) return;
    setLoading(true);
    listAmountConfigs(classId)
      .then(setConfigs)
      .finally(() => setLoading(false));
  };
  useEffect(load, [classId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await saveAmountConfig({
        classId: Number(classId),
        groupId: Number(form.groupId),
        categoryId: Number(form.categoryId),
        feeHeadId: Number(form.feeHeadId),
        feeSubHeadId: Number(form.feeSubHeadId),
        feeAmount: Number(form.feeAmount),
        fineAmount: Number(form.fineAmount) || 0,
      });
      setSuccess("Fee amount saved.");
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save.");
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete amount config for "${row.feeHeadName} / ${row.feeSubHeadName}"?`)) return;
    try {
      await deleteAmountConfig(row.configId);
      setSuccess("Deleted.");
      setConfigs((prev) => prev.filter((c) => c.configId !== row.configId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  return (
    <>
      <div className="attendance-filters">
        <label>
          Class
          <select value={classId} onChange={(e) => setClassId(e.target.value)}>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <form onSubmit={handleSubmit} className="user-form" style={{ marginBottom: 24 }}>
        <div className="attendance-filters">
          <label>
            Group
            <select value={form.groupId} onChange={(e) => setForm({ ...form, groupId: e.target.value })} required>
              <option value="" disabled>
                Select
              </option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Student Category
            <select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })} required>
              <option value="" disabled>
                Select
              </option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Fee Head
            <select value={form.feeHeadId} onChange={(e) => setForm({ ...form, feeHeadId: e.target.value })} required>
              <option value="" disabled>
                Select
              </option>
              {feeHeads.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Fee Sub-Head
            <select value={form.feeSubHeadId} onChange={(e) => setForm({ ...form, feeSubHeadId: e.target.value })} required>
              <option value="" disabled>
                Select
              </option>
              {feeSubHeads.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="attendance-filters">
          <label>
            Fee Amount
            <input type="number" value={form.feeAmount} onChange={(e) => setForm({ ...form, feeAmount: e.target.value })} required />
          </label>
          <label>
            Fine Amount
            <input type="number" value={form.fineAmount} onChange={(e) => setForm({ ...form, fineAmount: e.target.value })} />
          </label>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn-primary">
            Save (creates or updates)
          </button>
        </div>
      </form>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[
            { key: "groupName", label: "Group" },
            { key: "categoryName", label: "Category" },
            { key: "feeHeadName", label: "Fee Head" },
            { key: "feeSubHeadName", label: "Sub-Head" },
            { key: "feeAmount", label: "Amount" },
            { key: "fineAmount", label: "Fine" },
          ]}
          rows={configs}
          actions={(row) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
              Delete
            </button>
          )}
        />
      )}
    </>
  );
}

function TimeTab({ feeHeads, feeSubHeads, setError, setSuccess }) {
  const [payableMonth, setPayableMonth] = useState(MONTHS[new Date().getMonth()]);
  const [payableYear, setPayableYear] = useState(String(new Date().getFullYear()));
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ feeHeadId: "", feeSubHeadId: "", feeActiveDate: "", fineActiveDate: "" });

  const load = () => {
    setLoading(true);
    listTimeConfigs(payableMonth, payableYear)
      .then(setConfigs)
      .finally(() => setLoading(false));
  };
  useEffect(load, [payableMonth, payableYear]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await saveTimeConfig({
        feeHeadId: Number(form.feeHeadId),
        feeSubHeadId: Number(form.feeSubHeadId),
        payableMonth,
        payableYear,
        feeActiveDate: form.feeActiveDate || null,
        fineActiveDate: form.fineActiveDate || null,
      });
      setSuccess("Fee time configuration saved.");
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save.");
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete time config for "${row.feeHeadName} / ${row.feeSubHeadName}"?`)) return;
    try {
      await deleteTimeConfig(row.id);
      setSuccess("Deleted.");
      setConfigs((prev) => prev.filter((c) => c.id !== row.id));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  return (
    <>
      <div className="attendance-filters">
        <label>
          Payable Month
          <select value={payableMonth} onChange={(e) => setPayableMonth(e.target.value)}>
            {MONTHS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label>
          Payable Year
          <input value={payableYear} onChange={(e) => setPayableYear(e.target.value)} style={{ width: 90 }} />
        </label>
      </div>

      <form onSubmit={handleSubmit} className="user-form" style={{ marginBottom: 24 }}>
        <div className="attendance-filters">
          <label>
            Fee Head
            <select value={form.feeHeadId} onChange={(e) => setForm({ ...form, feeHeadId: e.target.value })} required>
              <option value="" disabled>
                Select
              </option>
              {feeHeads.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Fee Sub-Head
            <select value={form.feeSubHeadId} onChange={(e) => setForm({ ...form, feeSubHeadId: e.target.value })} required>
              <option value="" disabled>
                Select
              </option>
              {feeSubHeads.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Fee Active Date
            <input type="date" value={form.feeActiveDate} onChange={(e) => setForm({ ...form, feeActiveDate: e.target.value })} />
          </label>
          <label>
            Fine Active Date
            <input type="date" value={form.fineActiveDate} onChange={(e) => setForm({ ...form, fineActiveDate: e.target.value })} />
          </label>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn-primary">
            Save (creates or updates)
          </button>
        </div>
      </form>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[
            { key: "feeHeadName", label: "Fee Head" },
            { key: "feeSubHeadName", label: "Sub-Head" },
            { key: "feeActiveDate", label: "Fee Active" },
            { key: "fineActiveDate", label: "Fine Active" },
          ]}
          rows={configs}
          actions={(row) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
              Delete
            </button>
          )}
        />
      )}
    </>
  );
}
